<template>
  <div class="instructor-form-page">
    <div class="d-flex">
      <nav id="breadcrumbs" class="mb-3">
        <ul>
          <li>
            <router-link :to="{ name: 'company.dashboard' }">
              <i class="uil-home-alt"></i>
            </router-link>
          </li>
          <li>{{ $t("general.instructors") }}</li>
          <li>
            {{
              isEditMode
                ? $t("general.edit_instructor")
                : $t("general.add_instructor")
            }}
          </li>
        </ul>
      </nav>
    </div>

    <div class="section-header mb-lg-2 border-0 uk-flex-middle">
      <div class="section-header-left">
        <h2 class="uk-heading-line text-left">
          <span>
            {{
              isEditMode
                ? $t("general.edit_instructor")
                : $t("general.add_instructor")
            }}
          </span>
        </h2>
      </div>
      <div class="section-header-right">
        <button class="btn btn-light" type="button" @click="goToList">
          <i class="uil-list-ul"></i> {{ $t("general.instructor_list") }}
        </button>
      </div>
    </div>

    <div v-if="formErrors.length" class="mb-3">
      <div
        v-for="(error, index) in formErrors"
        :key="`instructor-error-${index}`"
        class="uk-alert-danger"
        uk-alert
      >
        <p class="mb-0 text-danger">
          <i class="uil-exclamation-triangle"></i> {{ error }}
        </p>
      </div>
    </div>

    <div v-if="loading" class="card">
      <default-loading></default-loading>
    </div>

    <form v-else class="card regular-form" @submit.prevent="saveInstructor">
      <div class="uk-grid-collapse uk-child-width-1-2@s" uk-grid>
        <div class="uk-padding-large instructor-form-column">
          <h4 class="instructor-form-section-title">
            <i class="uil-user-circle"></i>
            {{ $t("general.user_and_type_information") }}
          </h4>

          <div class="form-group">
            <label class="col-form-label">
              {{ $t("general.user") }} <span class="required">*</span>
            </label>
            <multiselect
              v-model="selectedUser"
              :options="userOptions"
              :loading="usersLoading"
              :disabled="isEditMode || usersLoading"
              :allow-empty="false"
              :close-on-select="true"
              :searchable="true"
              label="name"
              track-by="id"
              :placeholder="$t('general.select_user')"
              :select-label="$t('general.select')"
              :selected-label="$t('general.selected')"
              class="instructor-user-select"
              deselect-label=""
            >
              <template #option="{ option }">
                <div class="instructor-user-option">
                  <img
                    :src="option.photo"
                    :alt="option.name"
                    @error="useFallbackPhoto"
                  />
                  <span>
                    <strong>{{ option.name }}</strong>
                    <small>{{
                      option.email || $t("general.email_not_found")
                    }}</small>
                  </span>
                </div>
              </template>
              <template #singleLabel="{ option }">
                <span>{{ option.name }} — {{ option.email }}</span>
              </template>
              <template #noResult>
                <div class="no-results">
                  {{ $t("general.no_results_found") }}
                </div>
              </template>
              <template #noOptions>
                <span>{{ $t("general.no_options") }}</span>
              </template>
            </multiselect>
          </div>

          <div v-if="selectedUser" class="selected-user-summary">
            <img
              :src="selectedUser.photo"
              :alt="selectedUser.name"
              @error="useFallbackPhoto"
            />
            <div>
              <strong>{{ selectedUser.name }}</strong>
              <span>{{
                selectedUser.email || $t("general.email_not_found")
              }}</span>
              <span v-if="selectedUser.title || selectedUser.department">
                {{
                  selectedUser.title || $t("general.title_not_specified")
                }}
                <template v-if="selectedUser.department">
                  · {{ selectedUser.department }}
                </template>
              </span>
            </div>
          </div>

          <div class="form-group mt-4">
            <label class="col-form-label" for="instructor-type">
              {{ $t("general.instructor_type") }}
              <span class="required">*</span>
            </label>
            <select
              id="instructor-type"
              v-model="form.type"
              class="form-control"
              required
            >
              <option :value="instructorTypes.INTERNAL">
                {{ $t("general.internal_instructor") }}
              </option>
              <option :value="instructorTypes.EXTERNAL">
                {{ $t("general.external_instructor") }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label class="col-form-label" for="instructor-institution">
              {{ $t("general.institution_name") }}
            </label>
            <input
              id="instructor-institution"
              v-model.trim="form.institutionName"
              class="form-control"
              type="text"
              :placeholder="$t('general.institution_name_placeholder')"
            />
          </div>

          <div class="form-group">
            <label class="col-form-label" for="instructor-status">
              {{ $t("general.status") }} <span class="required">*</span>
            </label>
            <select
              id="instructor-status"
              v-model.number="form.statusId"
              class="form-control"
              required
            >
              <option :value="instructorStatuses.ACTIVE">
                {{ $t("general.active") }}
              </option>
              <option :value="instructorStatuses.PASSIVE">
                {{ $t("general.passive") }}
              </option>
            </select>
          </div>
        </div>

        <div
          class="uk-padding-large instructor-form-column instructor-form-column--right"
        >
          <h4 class="instructor-form-section-title">
            <i class="uil-award"></i>
            {{ $t("general.expertise_information") }}
          </h4>

          <div class="form-group">
            <label class="col-form-label" for="instructor-bio">{{
              $t("general.about")
            }}</label>
            <textarea
              id="instructor-bio"
              v-model.trim="form.bio"
              class="form-control"
              rows="5"
              :placeholder="$t('general.instructor_bio_placeholder')"
            ></textarea>
          </div>

          <div class="form-group">
            <label class="col-form-label" for="instructor-expertise">
              {{ $t("general.expertise_areas") }}
            </label>
            <multiselect
              id="instructor-expertise"
              ref="expertiseTagSelect"
              v-model="form.expertiseAreas"
              :allow-empty="true"
              :close-on-select="false"
              :hide-selected="true"
              :multiple="true"
              :max-height="80"
              :open-direction="'bottom'"
              :options="expertiseOptions"
              :placeholder="form.expertiseAreas.length ? '' : $t('general.add')"
              :searchable="true"
              :show-no-options="false"
              :show-no-results="false"
              :tag-placeholder="$t('general.add')"
              :taggable="true"
              class="instructor-tag-select"
              deselect-label=""
              select-label=""
              selected-label=""
              @tag="addExpertiseArea"
            />
          </div>

          <div class="form-group">
            <label class="col-form-label" for="instructor-certifications">
              {{ $t("general.educations_and_certificates") }}
            </label>
            <multiselect
              id="instructor-certifications"
              ref="educationCertificateTagSelect"
              v-model="form.educationsCertificates"
              :allow-empty="true"
              :close-on-select="false"
              :hide-selected="true"
              :multiple="true"
              :max-height="80"
              :open-direction="'bottom'"
              :options="educationCertificateOptions"
              :placeholder="
                form.educationsCertificates.length ? '' : $t('general.add')
              "
              :searchable="true"
              :show-no-options="false"
              :show-no-results="false"
              :tag-placeholder="$t('general.add')"
              :taggable="true"
              class="instructor-tag-select"
              deselect-label=""
              select-label=""
              selected-label=""
              @tag="addEducationCertificate"
            />
          </div>

          <div class="form-group">
            <label class="col-form-label" for="instructor-experience">
              {{ $t("general.experience") }}
            </label>
            <div class="input-group">
              <input
                id="instructor-experience"
                v-model.number="form.experienceYears"
                class="form-control"
                type="number"
                min="0"
                max="100"
              />
              <div class="input-group-append">
                <span class="input-group-text">{{ $t("general.year") }}</span>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="col-form-label" for="instructor-linkedin">
              {{ $t("general.linkedin_profile") }}
            </label>
            <input
              id="instructor-linkedin"
              v-model.trim="form.linkedinUrl"
              class="form-control"
              type="url"
              placeholder="https://www.linkedin.com/in/..."
            />
          </div>
        </div>
      </div>

      <div class="instructor-form-note">
        <i class="uil-info-circle"></i>
        <span>
          {{ $t("general.instructor_user_information_note") }}
        </span>
      </div>

      <div class="instructor-form-actions">
        <button class="btn btn-light" type="button" @click="goToList">
          {{ $t("general.give_up") }}
        </button>
        <button class="btn btn-default" type="submit" :disabled="saving">
          <i
            :class="saving ? 'uil-spinner-alt instructor-spin' : 'uil-check'"
          ></i>
          {{
            saving
              ? $t("general.saving")
              : isEditMode
              ? $t("general.save_changes")
              : $t("general.add_instructor")
          }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import instructorService, {
  INSTRUCTOR_STATUSES,
  INSTRUCTOR_TYPES,
} from "@/core/services/instructor.service";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";

const FALLBACK_PHOTO = "https://cdn.anzera.com.tr/softinya/not-found.png";

const emptyForm = () => ({
  type: INSTRUCTOR_TYPES.INTERNAL,
  institutionName: "",
  expertiseAreas: [],
  bio: "",
  experienceYears: null,
  educationsCertificates: [],
  linkedinUrl: "",
  statusId: INSTRUCTOR_STATUSES.ACTIVE,
});

export default {
  name: "InstructorAdd",
  components: { DefaultLoading, Multiselect },
  data() {
    return {
      form: emptyForm(),
      selectedUser: null,
      userOptions: [],
      expertiseOptions: [],
      educationCertificateOptions: [],
      loading: false,
      usersLoading: false,
      saving: false,
      formErrors: [],
      instructorTypes: INSTRUCTOR_TYPES,
      instructorStatuses: INSTRUCTOR_STATUSES,
    };
  },
  computed: {
    isEditMode() {
      return this.$route.name === "company.instructors.edit";
    },
  },
  methods: {
    async initializePage() {
      this.loading = true;
      this.formErrors = [];

      try {
        if (this.isEditMode) {
          await this.loadInstructor();
        } else {
          await this.loadUsers();
        }
      } catch (error) {
        this.formErrors = [
          this.getErrorMessage(
            error,
            this.$t("general.page_information_load_failed")
          ),
        ];
      } finally {
        this.loading = false;
      }
    },
    async loadUsers() {
      this.usersLoading = true;
      try {
        this.userOptions = await instructorService.listUsers();
      } finally {
        this.usersLoading = false;
      }
    },
    async loadInstructor() {
      const instructor = await instructorService.get(this.$route.params.id);
      const expertiseAreas = [...(instructor.expertiseAreas || [])];
      const educationsCertificates = [
        ...(instructor.educationsCertificates || []),
      ];
      this.selectedUser = instructor.user;
      this.userOptions = [instructor.user];
      this.expertiseOptions = [...expertiseAreas];
      this.educationCertificateOptions = [...educationsCertificates];
      this.form = {
        type: instructor.type,
        institutionName: instructor.institutionName,
        expertiseAreas,
        bio: instructor.bio,
        experienceYears: instructor.experienceYears,
        educationsCertificates,
        linkedinUrl: instructor.linkedinUrl,
        statusId: instructor.statusId,
      };
    },
    addExpertiseArea(value) {
      this.addTag("expertiseAreas", "expertiseOptions", value);
      this.closeTagSelect("expertiseTagSelect");
    },
    addEducationCertificate(value) {
      this.addTag(
        "educationsCertificates",
        "educationCertificateOptions",
        value
      );
      this.closeTagSelect("educationCertificateTagSelect");
    },
    closeTagSelect(refName) {
      this.$nextTick(() => {
        this.$refs[refName]?.deactivate();
      });
    },
    addTag(formField, optionsField, value) {
      const tag = String(value || "").trim();
      if (!tag) return;

      const exists = this.form[formField].some(
        (item) => item.toLocaleLowerCase("tr-TR") === tag.toLocaleLowerCase("tr-TR")
      );
      if (exists) return;

      this[optionsField].push(tag);
      this.form[formField].push(tag);
    },
    validateForm() {
      const errors = [];
      if (!this.selectedUser) {
        errors.push(this.$t("general.user_selection_required"));
      }
      if (!Object.values(INSTRUCTOR_TYPES).includes(this.form.type)) {
        errors.push(this.$t("general.valid_instructor_type_required"));
      }
      if (![0, 1].includes(Number(this.form.statusId))) {
        errors.push(this.$t("general.valid_status_required"));
      }
      this.formErrors = errors;
      return errors.length === 0;
    },
    buildPayload() {
      const payload = {
        type: this.form.type,
        institution_name: this.form.institutionName || null,
        expertise_areas: [...this.form.expertiseAreas],
        bio: this.form.bio || null,
        experience_years:
          this.form.experienceYears === null || this.form.experienceYears === ""
            ? null
            : Number(this.form.experienceYears),
        educations_certificates: [...this.form.educationsCertificates],
        linkedin_url: this.form.linkedinUrl || null,
        status_id: Number(this.form.statusId),
      };

      if (!this.isEditMode) payload.user_id = this.selectedUser.id;
      return payload;
    },
    async saveInstructor() {
      if (!this.validateForm()) return;

      this.saving = true;
      this.formErrors = [];
      try {
        let savedInstructor;
        if (this.isEditMode) {
          savedInstructor = await instructorService.update(
            this.$route.params.id,
            this.buildPayload()
          );
        } else {
          savedInstructor = await instructorService.create(
            this.buildPayload()
          );
        }

        this.$router.push({
          name: "company.instructors.list",
          query: {
            saved: savedInstructor.id || this.$route.params.id,
            action: this.isEditMode ? "updated" : "created",
          },
        });
      } catch (error) {
        this.formErrors = [
          this.getErrorMessage(
            error,
            this.$t("general.instructor_save_failed")
          ),
        ];
        window.scrollTo({ top: 0, behavior: "smooth" });
      } finally {
        this.saving = false;
      }
    },
    getErrorMessage(error, fallback) {
      const responseData = error?.response?.data;
      if (responseData?.message) return responseData.message;
      if (responseData?.error) return responseData.error;
      if (responseData?.errors) {
        return Object.values(responseData.errors).flat().join(" ");
      }
      return error?.message || fallback;
    },
    useFallbackPhoto(event) {
      if (event.target.src !== FALLBACK_PHOTO)
        event.target.src = FALLBACK_PHOTO;
    },
    goToList() {
      this.$router.push({ name: "company.instructors.list" });
    },
  },
  mounted() {
    this.initializePage();
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.instructor-form-description {
  margin: -5px 0 0;
  color: #7b8796;
  font-size: 13px;
}

.instructor-form-column {
  min-width: 0;
}

.instructor-form-column--right {
  border-left: 1px solid #edf0f3;
}

.instructor-form-section-title {
  margin-bottom: 22px;
  color: #344054;
  font-size: 15px;
}

.instructor-form-section-title i {
  margin-right: 5px;
  color: #4f73c3;
}

.instructor-tag-select ::v-deep .multiselect__input,
.instructor-user-select ::v-deep .multiselect__input {
  border: 0 !important;
  outline: 0;
  box-shadow: none !important;
}

.instructor-user-option {
  display: flex;
  align-items: center;
  gap: 10px;
}

.instructor-user-option img {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #eef2f6;
  object-fit: cover;
}

.instructor-user-option span {
  display: flex;
  flex-direction: column;
}

.instructor-user-option small {
  color: #8491a3;
}

.selected-user-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
}

.selected-user-summary img {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #eef2f6;
  object-fit: cover;
  object-position: center top;
}

.selected-user-summary div {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.selected-user-summary span {
  color: #718096;
  font-size: 12px;
}

.instructor-form-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 40px 20px;
  padding: 12px;
  border-radius: 8px;
  background: #edf3ff;
  color: #356ac3;
  font-size: 11px;
}

.instructor-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 20px 40px;
  border-top: 1px solid #edf0f3;
  background: #fafbfc;
}

.instructor-spin {
  display: inline-block;
  animation: instructor-spin 0.8s linear infinite;
}

@keyframes instructor-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .instructor-form-column {
    padding: 24px !important;
  }

  .instructor-form-column--right {
    border-top: 1px solid #edf0f3;
    border-left: 0;
  }

  .instructor-form-note {
    margin: 0 24px 20px;
  }

  .instructor-form-actions {
    padding: 20px 24px;
  }
}
</style>
